import { Schedule as ScheduleIcon } from "@mui/icons-material";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { PageHeader } from "../components/page-header";
import { DayRunsDrawer } from "../components/status-page/day-runs-drawer";
import { OverallStatusBanner } from "../components/status-page/overall-status-banner";
import { ScheduleStatusCard } from "../components/status-page/schedule-status-card";
import { TimeRangeSelector } from "../components/status-page/time-range-selector";
import { useApiQuery } from "../hooks/use-api-query";
import { useUrlFilters } from "../hooks/use-url-filters";
import type { DailyStatus, StatusPageResponse } from "../types";

interface DrawerState {
  open: boolean;
  selectedDay: DailyStatus | null;
  scheduleId: string;
  scheduleName: string;
  checkId?: string;
  checkName?: string;
}

export default function StatusPage() {
  const [filters, setFilters] = useUrlFilters({
    days: 90,
  });

  const [drawerState, setDrawerState] = useState<DrawerState>({
    open: false,
    selectedDay: null,
    scheduleId: "",
    scheduleName: "",
  });

  const { data, isLoading } = useApiQuery<StatusPageResponse>(`/status-page?days=${filters.days}`);

  const handleTimeRangeChange = (days: number) => {
    setFilters({ days });
  };

  const handleScheduleDayClick = useCallback(
    (scheduleId: string, scheduleName: string) => (status: DailyStatus) => {
      setDrawerState({
        open: true,
        selectedDay: status,
        scheduleId,
        scheduleName,
        checkId: undefined,
        checkName: undefined,
      });
    },
    [],
  );

  const handleCheckDayClick = useCallback(
    (scheduleId: string, scheduleName: string) =>
      (status: DailyStatus, checkId: string, checkName: string) => {
        setDrawerState({
          open: true,
          selectedDay: status,
          scheduleId,
          scheduleName,
          checkId,
          checkName,
        });
      },
    [],
  );

  const handleCloseDrawer = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, open: false, selectedDay: null }));
  }, []);

  return (
    <>
      <Stack spacing={3}>
        <PageHeader title="Status" subtitle="System uptime monitoring" />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
          <Box sx={{ flex: 1 }}>
            <OverallStatusBanner status={data?.overall_status} isLoading={isLoading} />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <TimeRangeSelector value={filters.days} onChange={handleTimeRangeChange} />
          </Box>
        </Stack>

        {isLoading ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : data?.schedules && data.schedules.length > 0 ? (
          <Stack spacing={0}>
            {data.schedules.map((schedule) => (
              <ScheduleStatusCard
                key={schedule.schedule_id}
                schedule={schedule}
                days={filters.days}
                selectedDate={
                  drawerState.scheduleId === schedule.schedule_id
                    ? drawerState.selectedDay?.date
                    : undefined
                }
                onScheduleDayClick={handleScheduleDayClick(
                  schedule.schedule_id,
                  schedule.schedule_name,
                )}
                onCheckDayClick={handleCheckDayClick(schedule.schedule_id, schedule.schedule_name)}
              />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <ScheduleIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }} gutterBottom>
              No schedules found
            </Typography>
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              Create check schedules to start monitoring your systems.
            </Typography>
          </Box>
        )}

        {data?.last_updated && (
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              textAlign: "right",
              display: "block",
            }}
          >
            Last updated: {new Date(data.last_updated).toLocaleString()}
          </Typography>
        )}
      </Stack>

      <DayRunsDrawer
        open={drawerState.open}
        onClose={handleCloseDrawer}
        selectedDay={drawerState.selectedDay}
        scheduleId={drawerState.scheduleId}
        scheduleName={drawerState.scheduleName}
        checkId={drawerState.checkId}
        checkName={drawerState.checkName}
      />
    </>
  );
}
